/* global process */
import template from "./document_scan_start.html";
import { manualDocumentCaptureState } from "../../../../base.routes";
import en from "./en.json";
import es from "./es.json";
import mixpanel from "mixpanel-browser";
import {
    UPLOAD_PENDING,
    UPLOAD_SUCCESS,
    FACING_MODE_ENVIRONMENT,
    UPLOAD_FAILED,
    UPLOAD_NOT_STARTED,
} from "../../../common/common.constants";
import { SID_LOOKUP } from "../../../../app.constants";
import { createCloudinaryContextString } from "../../../common/common.utils";

export const MAX_OCR_IMAGE_DIMENSION = 2000;

class DocumentScanStartController {
    /* @ngInject */
    constructor(
        $document,
        ToastService,
        $http,
        $state,
        $window,
        $q,
        CanvasService,
        ImageShrinkService,
        IdentificationDocumentService,
        IdentificationDocumentTypeService,
        $translate,
        $timeout,
    ) {
        this.$document = $document;
        this.ToastService = ToastService;
        this.$http = $http;
        this.$state = $state;
        this.$window = $window;
        this.$q = $q;
        this.CanvasService = CanvasService;
        this.ImageShrinkService = ImageShrinkService;
        this.IdentificationDocumentService = IdentificationDocumentService;
        this.IdentificationDocumentTypeService =
            IdentificationDocumentTypeService;
        this.$translate = $translate;
        this.$timeout = $timeout;

        this.UPLOAD_PENDING = UPLOAD_PENDING;
        this.UPLOAD_SUCCESS = UPLOAD_SUCCESS;
        this.UPLOAD_FAILED = UPLOAD_FAILED;
        this.UPLOAD_NOT_STARTED = UPLOAD_NOT_STARTED;
        this.PHOTO_SIDE_FRONT = "front";
        this.PHOTO_SIDE_BACK = "back";
        this.cameraAllowed = true;

        this.IDOptions = {};
        this.uploadStatus = UPLOAD_NOT_STARTED;
        this.photoSide = this.PHOTO_SIDE_FRONT;
        this.frontImageId = undefined;
        this.backImageId = undefined;
        this.documentType = undefined;
        this.MANUAL_CAPTURE_METHOD = "manual";
        this.manualDocumentTypes = [];
    }

    $onInit() {
        let ctrl = this;
        return ctrl.loadManualDocumentTypes();
    }

    loadManualDocumentTypes() {
        let ctrl = this;
        ctrl.loadingManualDocumentTypes = true;
        ctrl.manualDocumentTypesLoadFailed = false;
        return ctrl.IdentificationDocumentTypeService.list({
            accessor__sid: ctrl.growerSid,
            active: true,
            capture_method: ctrl.MANUAL_CAPTURE_METHOD,
        })
            .then(
                function (documentTypes) {
                    ctrl.manualDocumentTypes = documentTypes.filter(
                        (documentType) =>
                            documentType.capture_method ===
                            ctrl.MANUAL_CAPTURE_METHOD,
                    );
                },
                function () {
                    ctrl.manualDocumentTypesLoadFailed = true;
                },
            )
            .finally(function () {
                ctrl.loadingManualDocumentTypes = false;
            });
    }

    openFilePicker() {
        let ctrl = this;
        let input = ctrl.$document[0].getElementById("idImage");
        input.value = "";
        input.click();
    }

    captureVideo() {
        let ctrl = this;
        let options = {
            video: {
                width: {
                    ideal: 1920,
                    max: MAX_OCR_IMAGE_DIMENSION,
                },
                height: {
                    ideal: 1080,
                    max: MAX_OCR_IMAGE_DIMENSION,
                },
                facingMode: FACING_MODE_ENVIRONMENT,
                frameRate: {
                    ideal: 30,
                },
            },
            audio: false,
        };
        ctrl.$q
            .when(ctrl.$window.navigator.mediaDevices.getUserMedia(options))
            .then(
                function (stream) {
                    if (ctrl.destroyed || !ctrl.usingBackupPhoto) {
                        for (let track of stream.getTracks()) {
                            track.stop();
                        }
                        return;
                    }
                    ctrl.cameraAllowed = true;
                    ctrl.video.srcObject = stream;
                    ctrl.video.play();
                },
                function () {
                    ctrl.cameraAllowed = false;
                },
            );
    }

    openBackupPhoto() {
        let ctrl = this;
        ctrl.usingBackupPhoto = true;
        ctrl.video = ctrl.$document[0].getElementById("video");
        ctrl.captureVideo();
    }

    supportsCapture() {
        let ctrl = this;
        var input = ctrl.$document[0].createElement("input");
        input.setAttribute("type", "file");
        input.setAttribute("capture", "user");
        return "capture" in input && input.capture === "user";
    }

    endVideoCapture() {
        let ctrl = this;
        if (!ctrl.video?.srcObject) {
            return;
        }
        for (let track of ctrl.video.srcObject.getTracks()) {
            track.stop();
        }
        ctrl.video.srcObject = null;
    }

    $onDestroy() {
        let ctrl = this;
        ctrl.destroyed = true;
        ctrl.endVideoCapture();
    }

    handleCameraPhoto(options) {
        let ctrl = this;
        ctrl.uploadStatus = ctrl.UPLOAD_PENDING;
        ctrl.shrinkAndUpload(options.file);
    }

    shrinkAndUpload(file) {
        let ctrl = this;
        return ctrl.ImageShrinkService.shrink(file, 1024 * 1024, {
            maxDimension: MAX_OCR_IMAGE_DIMENSION,
        }).then(
            function (blob) {
                ctrl.upload(blob);
            },
            function () {
                ctrl.uploadStatus = ctrl.UPLOAD_FAILED;
                ctrl.ToastService.create("documentScanStart.FAILURE_TOAST");
            },
        );
    }

    takePhoto() {
        let ctrl = this;
        ctrl.uploadStatus = ctrl.UPLOAD_PENDING;
        let canvas = ctrl.$document[0].getElementById("canvas");
        let video = ctrl.video;

        // Set image
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        let context = canvas.getContext("2d", { alpha: false });
        video.pause();

        const videoWidth = video.videoWidth;
        const videoHeight = video.videoHeight;
        const offsetHeight = video.offsetHeight;
        const offsetWidth = video.offsetWidth;

        // Calculate aspect ratios
        const videoAspectRatio = videoWidth / videoHeight;
        const displayedAspectRatio = offsetWidth / offsetHeight;

        let cropWidth, cropHeight, cropX, cropY, finalWidth, finalHeight;

        // Determine the cropping dimensions and position
        if (videoAspectRatio > displayedAspectRatio) {
            // Video is wider than the displayed area
            cropHeight = videoHeight;
            cropWidth = cropHeight * displayedAspectRatio;
            cropX = (videoWidth - cropWidth) / 2;
            cropY = 0;
        } else {
            // Video is taller than the displayed area
            cropWidth = videoWidth;
            cropHeight = cropWidth / displayedAspectRatio;
            cropX = 0;
            cropY = (videoHeight - cropHeight) / 2;
        }
        canvas.width = cropWidth;
        canvas.height = cropHeight;

        // Calculate the horizontal cropping offset (since the video is centered)
        context.drawImage(
            video,
            cropX,
            cropY,
            cropWidth,
            cropHeight,
            0,
            0,
            cropWidth,
            cropHeight,
        );

        ctrl.CanvasService.getBlob(canvas).then(
            function (blob) {
                ctrl.shrinkAndUpload(blob);
            },
            function () {
                ctrl.uploadStatus = ctrl.UPLOAD_FAILED;
                ctrl.ToastService.create("documentScanStart.FAILURE_TOAST");
            },
        );
    }

    resetCapture() {
        let ctrl = this;
        ctrl.endVideoCapture();
        ctrl.photoSide = ctrl.PHOTO_SIDE_FRONT;
        ctrl.frontImageId = undefined;
        ctrl.backImageId = undefined;
        ctrl.documentType = undefined;
        ctrl.usingBackupPhoto = false;
        ctrl.uploadStatus = ctrl.UPLOAD_NOT_STARTED;
        ctrl.errorMessage = undefined;
        ctrl.IDOptions = {};
    }

    startManualCapture() {
        let ctrl = this;
        ctrl.resetCapture();
        return ctrl.$state.go(manualDocumentCaptureState, {
            user_sid: ctrl.growerMembership.user,
            grower_sid: ctrl.growerSid,
            grower_membership_sid: ctrl.growerMembership.sid,
            job_application: ctrl.jobApplicationSid,
        });
    }

    startNewDocumentScan() {
        let ctrl = this;
        ctrl.resetCapture();
        ctrl.takePhotoHandler();
    }

    takePhotoHandler() {
        let ctrl = this;
        ctrl.IDOptions = {};
        ctrl.uploadStatus = ctrl.UPLOAD_NOT_STARTED;
        ctrl.errorMessage = undefined;

        let clientSupportsCapture = ctrl.supportsCapture();
        if (clientSupportsCapture) {
            ctrl.openFilePicker();
        } else {
            ctrl.openBackupPhoto();
        }
    }

    upload(file) {
        let ctrl = this;
        let fd = new FormData();
        fd.append("file", file);
        fd.append(
            "upload_preset",
            process.env.CUSTOM_DOCUMENT_SCAN_PHOTO_UPLOAD_PRESET,
        );
        fd.append(
            "folder",
            "external_documents/" +
                ctrl.growerSid +
                "/" +
                ctrl.growerMembershipSid,
        );
        // TODO: add job application context for later use
        ctrl.$http
            .post("https://api.cloudinary.com/v1_1/harvust/image/upload", fd, {
                headers: { "Content-Type": undefined },
                skipAuthorization: true,
            })
            .then(
                function (res) {
                    let data = res.data;
                    let [valid, toastMessage] = isValidPhoto(data);
                    if (!valid) {
                        ctrl.uploadStatus = UPLOAD_FAILED;
                        ctrl.errorMessage = toastMessage;
                        return;
                    }

                    ctrl.handleSuccessfulUpload(data);
                },
                function () {
                    ctrl.ToastService.create("documentScanStart.FAILURE_TOAST");
                    ctrl.$state.reload();
                },
            );
    }

    handleSuccessfulUpload(data) {
        let ctrl = this;
        if (ctrl.photoSide === ctrl.PHOTO_SIDE_FRONT) {
            ctrl.frontImageId = data.public_id;
            ctrl.IdentificationDocumentTypeService.determine({
                owner: ctrl.growerSid,
                front_image_id: ctrl.frontImageId,
            }).then(
                function (documentType) {
                    ctrl.documentType = documentType;
                    if (ctrl.documentType.requires_back_photo) {
                        ctrl.photoSide = ctrl.PHOTO_SIDE_BACK;
                        ctrl.uploadStatus = ctrl.UPLOAD_NOT_STARTED;
                        ctrl.errorMessage = undefined;
                        if (ctrl.usingBackupPhoto) {
                            ctrl.video.play();
                        }
                    } else {
                        ctrl.createIdentificationDocument();
                    }
                },
                function (reason) {
                    if (reason && reason.status === 404) {
                        ctrl.ToastService.create(
                            "documentScanStart.DOCUMENT_TYPE_NOT_IDENTIFIED_TOAST",
                        );
                        ctrl.$state.reload();
                        return;
                    }
                    ctrl.uploadStatus = ctrl.UPLOAD_FAILED;
                    ctrl.errorMessage = "documentScanStart.FAILURE_TOAST";
                },
            );
            return;
        }

        ctrl.backImageId = data.public_id;
        ctrl.createIdentificationDocument();
    }

    createIdentificationDocument() {
        let ctrl = this;
        let documentTypeSid = ctrl.documentType[SID_LOOKUP];
        let payload = {
            owner: ctrl.growerSid,
            subject: ctrl.growerMembership.user,
            grower_membership: ctrl.growerMembership[SID_LOOKUP],
            document_type: documentTypeSid,
            front_image_id: ctrl.frontImageId,
        };
        if (ctrl.backImageId) {
            payload.back_image_id = ctrl.backImageId;
        }

        ctrl.IdentificationDocumentService.create(payload).then(
            function () {
                ctrl.endVideoCapture();
                ctrl.usingBackupPhoto = false;
                ctrl.uploadStatus = UPLOAD_SUCCESS;
                mixpanel.track("Document scanned", {
                    company_sid: ctrl.growerSid,
                    self: ctrl.account.sid === ctrl.growerMembership.user,
                });
            },
            function () {
                ctrl.uploadStatus = UPLOAD_FAILED;
                ctrl.errorMessage = "documentScanStart.FAILURE_TOAST";
            },
        );
    }

    currentTakePhotoText() {
        let ctrl = this;
        if (ctrl.photoSide === ctrl.PHOTO_SIDE_BACK) {
            return "documentScanStart.TAKE_BACK_PHOTO";
        }
        return "documentScanStart.TAKE_FRONT_PHOTO";
    }

    currentRetakePhotoText() {
        let ctrl = this;
        if (ctrl.photoSide === ctrl.PHOTO_SIDE_BACK) {
            return "documentScanStart.RETAKE_BACK_PHOTO";
        }
        return "documentScanStart.RETAKE_FRONT_PHOTO";
    }
}

export default {
    controller: DocumentScanStartController,
    bindings: {
        account: "<",
        userSid: "<",
        growerSid: "<",
        growerMembershipSid: "<",
        growerMembership: "<",
        jobApplicationSid: "<",
    },
    template: template,
    name: "documentScanStart",
    translations: { en, es },
};

function isValidPhoto(data) {
    let quality = data.quality_analysis.focus;
    if (quality < 0.4) {
        return [false, "documentScanStart.POOR_QUALITY_TOAST"];
    } else {
        return [true, undefined];
    }
}
